import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useStyles } from './FlyoutCasProgramSetup.styles'
import { ButtonV2, CFSButtonV2, TextInputV4 } from '@labrav/react-components'
import { FormikProps, useFormikContext } from 'formik'
import type {
  FlyOutCloudSettingsFormValues,
  SellerMetadataFieldErrors,
  SellerMetadataTouched,
} from '../../types'
import {
  emptySpaceCharsAtStartAndEndRegex,
  flyOutDataFeedsCFTLink,
  flyoutFormDebounceTime,
  newDataFeedProgramLink,
} from '../../../../common/utils/constants'
import { ExternalLink, NoteV2 } from '@labrav/react-components'
import type { TestConnectionStatusProps } from '../../../../modules/flyOutOnboarding/types'
import { useDispatch } from '../../../../store'
import {
  flyOutTestConnection,
  flyOutTestConnectionRoleAndDatafeeds,
} from '../../../../modules/flyOutOnboarding/action'
import { ShowConnectionStatuses } from '../../../FlyOutCloudSettings/FlyOutArnDetails/FlyOutArnDetails'
import { Typography } from '@mui/material'
import { useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleCheck,
  faCircleDashed,
  faCircleXmark,
} from '@fortawesome/pro-regular-svg-icons'
import { faLoader } from '@fortawesome/pro-duotone-svg-icons'
import { useFlagValue } from '@labrav/flags'
import {
  TERRAFORM_VALIDATION_STEPS,
  type TerraformValidationStepStatus,
} from '../../../FlyOutCloudSettings/FlyoutCasProgramSetup/constants'

export type FlyoutCasProgramSetupAccountProps = {
  showCloudFormationDataFeedsSetup?: boolean
  enableDataFeedsRevampBehavior?: boolean
}

export const FlyoutCasProgramSetup: React.FC<
  FlyoutCasProgramSetupAccountProps
> = ({
  showCloudFormationDataFeedsSetup = true,
  enableDataFeedsRevampBehavior = false,
}) => {
  const { classes, cx } = useStyles({ showCloudFormationDataFeedsSetup })
  const dispatch = useDispatch()
  const { accountId: accountIdFromParams } = useParams<{ accountId: string }>()
  const accountId = accountIdFromParams
  const {
    values,
    errors,
    touched,
    status,
    setStatus,
    handleBlur,
    handleChange,
    setFieldTouched,
    setFieldValue,
    setFieldError,
    validateForm,
  } = useFormikContext() as FormikProps<FlyOutCloudSettingsFormValues>
  const [testConnectionStatus, setTestConnectionStatus] =
    useState<TestConnectionStatusProps>({
      status: 'standBy',
    })
  const testConnectionDebounceRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)
  const statusRef = useRef(status)
  statusRef.current = status
  const { value: isMarketplaceCloudSettingsRevampEnabled } = useFlagValue(
    'marketplaceCloudSettingsRevamp'
  )
  const { value: isOnboardingImprovementsEnabled } = useFlagValue(
    'onboardingImprovements'
  )

  const { awsKmsKeyArn, awsCasS3BucketName, awsCasSnsTopic } =
    values.sellerMetadata
  const previousAwsRoleArnRef = useRef(values.sellerMetadata.awsRoleArn || '')
  const previousAwsCasS3BucketNameRef = useRef(
    values.sellerMetadata.awsCasS3BucketName || ''
  )
  const stackType = values.stackType
  const isTerraformStack =
    Boolean(isOnboardingImprovementsEnabled) && stackType === 'terraform'
  const settingsTouched = touched?.sellerMetadata as
    | SellerMetadataTouched
    | undefined
  const settingsError = errors?.sellerMetadata as
    | SellerMetadataFieldErrors
    | undefined
  const rootSettingsError = errors as { terraformConnectionValidated?: string }
  const rootSettingsTouched = touched as {
    terraformConnectionValidated?: boolean
  }
  const s3BucketArnFilled = !!awsCasS3BucketName
  const showAwsCasS3BucketNameError =
    !!(settingsTouched?.awsCasS3BucketName || s3BucketArnFilled) &&
    settingsError?.awsCasS3BucketName
  const failedConnectionField = (
    status as { failedConnectionField?: 'awsRoleArn' | 'awsCasS3BucketName' }
  )?.failedConnectionField
  const arnConnectionStatus = (status as { arnConnectionStatus?: string })
    ?.arnConnectionStatus
  const isS3BlockedByArnFailure =
    enableDataFeedsRevampBehavior &&
    showCloudFormationDataFeedsSetup &&
    arnConnectionStatus === 'failure'
  const isS3ConnectionFailure = showCloudFormationDataFeedsSetup
    ? enableDataFeedsRevampBehavior && testConnectionStatus.status === 'failure'
    : testConnectionStatus.status === 'failure' &&
      failedConnectionField === 'awsCasS3BucketName'

  const [terraformValidationSteps, setTerraformValidationSteps] = useState(
    TERRAFORM_VALIDATION_STEPS
  )
  const [loading, setLoading] = useState(false)
  const [terraformValidationPanelVisible, setTerraformValidationPanelVisible] =
    useState(false)

  useEffect(() => {
    if (!isTerraformStack) {
      return
    }
    if (values.terraformConnectionValidated !== undefined) {
      return
    }
    void setFieldValue('terraformConnectionValidated', false)
  }, [isTerraformStack, values.terraformConnectionValidated, setFieldValue])

  useEffect(() => {
    if (!isTerraformStack) {
      setTerraformValidationPanelVisible(false)
      return
    }
    if (values.terraformConnectionValidated === true) {
      setTerraformValidationPanelVisible(true)
      setTerraformValidationSteps(
        TERRAFORM_VALIDATION_STEPS.map(step => ({
          ...step,
          status: 'success' as TerraformValidationStepStatus,
        }))
      )
      return
    }
    setTerraformValidationPanelVisible(false)
    setTerraformValidationSteps(TERRAFORM_VALIDATION_STEPS)
    setLoading(false)
  }, [isTerraformStack, values.terraformConnectionValidated])

  useEffect(() => {
    if (!enableDataFeedsRevampBehavior) {
      return
    }
    if (showCloudFormationDataFeedsSetup) {
      return
    }
    if (isTerraformStack) {
      return
    }
    setTestConnectionStatus({ status: 'standBy' })
    setStatus?.({
      ...(status as Record<string, unknown>),
      failedConnectionField: undefined,
    })
    if (testConnectionDebounceRef.current) {
      clearTimeout(testConnectionDebounceRef.current)
    }
    const s3BucketArn = values.sellerMetadata.awsCasS3BucketName || ''
    const roleArn = values.sellerMetadata.awsRoleArn || ''

    if (s3BucketArn && !roleArn) {
      void setFieldTouched('sellerMetadata.awsRoleArn', true, true)
    }

    if (!s3BucketArn || !roleArn) {
      return
    }

    if (!emptySpaceCharsAtStartAndEndRegex.test(s3BucketArn)) {
      return
    }

    if (settingsError?.awsCasS3BucketName) {
      return
    }

    const changedField: 'awsRoleArn' | 'awsCasS3BucketName' =
      previousAwsRoleArnRef.current !== roleArn
        ? 'awsRoleArn'
        : 'awsCasS3BucketName'

    testConnectionDebounceRef.current = setTimeout(() => {
      void dispatch(
        flyOutTestConnectionRoleAndDatafeeds(
          accountId || '',
          roleArn,
          s3BucketArn,
          connectionStatus => {
            setTestConnectionStatus(connectionStatus)
            setStatus?.({
              ...(status as Record<string, unknown>),
              combinedConnectionStatus: connectionStatus.status,
              failedConnectionField:
                connectionStatus.status !== 'success'
                  ? changedField
                  : undefined,
            })
          }
        )
      )
    }, flyoutFormDebounceTime)
    previousAwsRoleArnRef.current = roleArn
    previousAwsCasS3BucketNameRef.current = s3BucketArn
  }, [
    showCloudFormationDataFeedsSetup,
    values.sellerMetadata.awsRoleArn,
    values.sellerMetadata.awsCasS3BucketName,
    settingsError?.awsCasS3BucketName,
  ])

  useEffect(() => {
    if (!enableDataFeedsRevampBehavior) {
      return
    }
    if (!showCloudFormationDataFeedsSetup) {
      return
    }
    if (isTerraformStack) {
      return
    }
    setTestConnectionStatus({ status: 'standBy' })
    setStatus?.({
      ...(status as Record<string, unknown>),
      failedConnectionField: undefined,
    })
    if (testConnectionDebounceRef.current) {
      clearTimeout(testConnectionDebounceRef.current)
    }
    const s3BucketArn = values.sellerMetadata.awsCasS3BucketName || ''
    const roleArn = values.sellerMetadata.awsRoleArn || ''
    if (!s3BucketArn) {
      return
    }
    if (!emptySpaceCharsAtStartAndEndRegex.test(s3BucketArn)) {
      return
    }
    if (settingsError?.awsCasS3BucketName) {
      return
    }
    if (arnConnectionStatus === 'failure') {
      setTestConnectionStatus({
        status: 'failure',
        error: 'S3 bucket connection failed because ARN connection failed.',
      })
      setStatus?.({
        ...(statusRef.current as Record<string, unknown>),
        failedConnectionField: 'awsCasS3BucketName' as const,
      })
      return
    }
    if (s3BucketArn && !roleArn) {
      void setFieldTouched('sellerMetadata.awsRoleArn', true, true)
    }
    if (!roleArn) {
      return
    }
    const onDataFeedsS3ConnectionResult = (
      connectionStatus: TestConnectionStatusProps
    ) => {
      setTestConnectionStatus(connectionStatus)
      if (connectionStatus.status === 'success') {
        setStatus?.({
          ...(statusRef.current as Record<string, unknown>),
          dataFeedsConnectionStatus: connectionStatus.status,
          failedConnectionField: undefined,
        })
      } else if (connectionStatus.status === 'failure') {
        setStatus?.({
          ...(statusRef.current as Record<string, unknown>),
          dataFeedsConnectionStatus: connectionStatus.status,
          failedConnectionField: 'awsCasS3BucketName' as const,
        })
      }
    }
    testConnectionDebounceRef.current = setTimeout(() => {
      void dispatch(
        flyOutTestConnectionRoleAndDatafeeds(
          accountId || '',
          roleArn,
          s3BucketArn,
          onDataFeedsS3ConnectionResult
        )
      )
    }, flyoutFormDebounceTime)
  }, [
    showCloudFormationDataFeedsSetup,
    (status as { arnConnectionStatus?: string })?.arnConnectionStatus,
    values.sellerMetadata.awsRoleArn,
    values.sellerMetadata.awsCasS3BucketName,
    settingsError?.awsCasS3BucketName,
  ])

  useEffect(() => {
    return () => {
      if (testConnectionDebounceRef.current) {
        clearTimeout(testConnectionDebounceRef.current)
      }
    }
  }, [])

  const cftLaunchLink = useMemo(() => {
    const baseUrl = flyOutDataFeedsCFTLink
    const s3BucketArn = String(values.sellerMetadata.awsCasS3BucketName)
    const arnRole = String(values.sellerMetadata.awsRoleArn)
    const kmsKeyArn = String(values.sellerMetadata.awsKmsKeyArn)
    const snsTopic = String(values.sellerMetadata.awsCasSnsTopic)
    return `${baseUrl}&param_KmsKeyArn=${encodeURIComponent(kmsKeyArn)}&param_LabraPlatformRole=${encodeURIComponent(arnRole)}&param_S3BucketArn=${encodeURIComponent(s3BucketArn)}&param_SnsTopicArn=${encodeURIComponent(snsTopic)}`
  }, [
    values.sellerMetadata.awsCasS3BucketName,
    values.sellerMetadata.awsRoleArn,
    values.sellerMetadata.awsKmsKeyArn,
    values.sellerMetadata.awsCasSnsTopic,
  ])

  const updateTerraformValidationStep = (
    stepIndex: number,
    status: TerraformValidationStepStatus,
    steps: typeof TERRAFORM_VALIDATION_STEPS
  ) =>
    steps.map((step, index) =>
      index === stepIndex ? { ...step, status } : step
    )

  const syncTerraformConnectionValidated = async (validated: boolean) => {
    await setFieldValue('terraformConnectionValidated', validated)
    if (validated) {
      setFieldError('terraformConnectionValidated', undefined)
    }
  }

  const resetTerraformConnectionValidation = async () => {
    await setFieldValue('terraformConnectionValidated', false)
    setFieldError('terraformConnectionValidated', undefined)
    setFieldTouched('terraformConnectionValidated', false, false)
    setTerraformValidationSteps(TERRAFORM_VALIDATION_STEPS)
    setTerraformValidationPanelVisible(false)
    setLoading(false)
  }

  const previousAwsRoleArnForTerraformResetRef = useRef(
    values.sellerMetadata.awsRoleArn || ''
  )
  const isTerraformArnTrackingInitializedRef = useRef(false)

  useEffect(() => {
    if (!isTerraformStack) {
      previousAwsRoleArnForTerraformResetRef.current =
        values.sellerMetadata.awsRoleArn || ''
      isTerraformArnTrackingInitializedRef.current = false
      return
    }
    const currentArn = values.sellerMetadata.awsRoleArn || ''
    if (!isTerraformArnTrackingInitializedRef.current) {
      previousAwsRoleArnForTerraformResetRef.current = currentArn
      isTerraformArnTrackingInitializedRef.current = true
      return
    }
    if (previousAwsRoleArnForTerraformResetRef.current === currentArn) {
      return
    }
    previousAwsRoleArnForTerraformResetRef.current = currentArn
    void resetTerraformConnectionValidation()
  }, [values.sellerMetadata.awsRoleArn, isTerraformStack])

  const handleValidateTerraformConnection = async () => {
    await setFieldTouched('sellerMetadata.awsRoleArn', true, true)
    if (values.sellerMetadata.awsCasS3BucketName) {
      await setFieldTouched('sellerMetadata.awsCasS3BucketName', true, true)
    }

    const formErrors = (await validateForm()) as {
      sellerMetadata?: {
        awsRoleArn?: string
        awsCasS3BucketName?: string
      }
    }

    if (
      formErrors.sellerMetadata?.awsRoleArn ||
      formErrors.sellerMetadata?.awsCasS3BucketName
    ) {
      await syncTerraformConnectionValidated(false)
      return
    }

    const cloudRoleArn = values.sellerMetadata.awsRoleArn
    const cloudCasS3BucketName = values.sellerMetadata.awsCasS3BucketName

    setTerraformValidationPanelVisible(true)
    setLoading(true)
    let steps = TERRAFORM_VALIDATION_STEPS.map(step => ({
      ...step,
      status: 'pending' as TerraformValidationStepStatus,
    }))
    setTerraformValidationSteps(steps)
    await syncTerraformConnectionValidated(false)

    const runDataFeedsValidation = async () => {
      if (!cloudCasS3BucketName) {
        return
      }

      await dispatch(
        flyOutTestConnectionRoleAndDatafeeds(
          accountId || '',
          cloudRoleArn || '',
          cloudCasS3BucketName,
          connectionStatus => {
            if (
              connectionStatus.status !== 'success' &&
              connectionStatus.status !== 'failure'
            ) {
              return
            }
            const s3Status: TerraformValidationStepStatus =
              connectionStatus.status === 'success' ? 'success' : 'failed'
            steps = updateTerraformValidationStep(1, s3Status, steps)
            steps = updateTerraformValidationStep(
              2,
              connectionStatus.status === 'success' ? 'success' : 'pending',
              steps
            )
            setTerraformValidationSteps(steps)
          }
        )
      )
    }

    try {
      await dispatch(
        flyOutTestConnection(
          accountId || '',
          cloudRoleArn || '',
          isMarketplaceCloudSettingsRevampEnabled,
          async connectionStatus => {
            if (
              connectionStatus.status !== 'success' &&
              connectionStatus.status !== 'failure'
            ) {
              return
            }

            const arnStatus: TerraformValidationStepStatus =
              connectionStatus.status === 'success' ? 'success' : 'failed'
            steps = updateTerraformValidationStep(0, arnStatus, steps)
            setTerraformValidationSteps(steps)

            if (connectionStatus.status === 'failure') {
              steps = updateTerraformValidationStep(1, 'failed', steps)
              steps = updateTerraformValidationStep(2, 'pending', steps)
              setTerraformValidationSteps(steps)
              return
            }

            await runDataFeedsValidation()
          }
        )
      )
      const allStepsValidated = steps.every(step => step.status === 'success')
      await syncTerraformConnectionValidated(allStepsValidated)
    } finally {
      setLoading(false)
    }
  }

  const terraformValidatedCount = terraformValidationSteps.filter(
    step => step.status === 'success'
  ).length

  const handleChangeField = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    await handleChange(event)
    if (isTerraformStack) {
      await resetTerraformConnectionValidation()
    }
  }

  const showTerraformValidationError =
    isTerraformStack &&
    !!rootSettingsTouched?.terraformConnectionValidated &&
    !!rootSettingsError?.terraformConnectionValidated

  return (
    <div className={classes.fieldsDataFeedsContainer}>
      {!enableDataFeedsRevampBehavior ? (
        <CFSButtonV2 cfLink={flyOutDataFeedsCFTLink} />
      ) : null}

      {showCloudFormationDataFeedsSetup ? (
        <>
          <div
            className={classes.subHeadingContainer}
            data-testid="data-feeds-text-and-link"
          >
            <NoteV2
              description={
                <>
                  Go to{' '}
                  <ExternalLink href={newDataFeedProgramLink}>
                    this link
                  </ExternalLink>{' '}
                  , copy the following values: S3 bucket ARN, KMS key ARN, SNS
                  topic ARN and paste each value into corresponding fields
                  below.
                </>
              }
            />
          </div>
        </>
      ) : null}
      <div className={classes.inputsColumn}>
        <div className={cx(classes.field, classes.fieldTopAligned)}>
          <TextInputV4
            keyName="sellerMetadata.awsCasS3BucketName"
            title="S3 bucket ARN"
            type="text"
            placeHolder=""
            value={awsCasS3BucketName || ''}
            isError={!!showAwsCasS3BucketNameError || isS3ConnectionFailure}
            onChange={handleChangeField}
            onBlur={handleBlur}
            isReadOnly={false}
            isRequired={false}
            errorText={showAwsCasS3BucketNameError || ''}
          />
        </div>
        <div className={classes.field} data-testid="kms-key-arn-input">
          <TextInputV4
            keyName="sellerMetadata.awsKmsKeyArn"
            title="KMS key ARN"
            type="string"
            placeHolder=""
            value={awsKmsKeyArn || ''}
            isError={
              !!(settingsTouched?.awsKmsKeyArn && settingsError?.awsKmsKeyArn)
            }
            onChange={handleChangeField}
            isReadOnly={false}
            isRequired={false}
            errorText={
              (!!settingsTouched?.awsKmsKeyArn &&
                settingsError?.awsKmsKeyArn) ||
              ''
            }
            showSpinner={true}
            onBlur={handleBlur}
          />
        </div>
        <div className={classes.field}>
          <TextInputV4
            keyName="sellerMetadata.awsCasSnsTopic"
            title="SNS topic ARN"
            type="string"
            placeHolder=""
            value={awsCasSnsTopic || ''}
            isError={
              !!(
                settingsTouched?.awsCasSnsTopic && settingsError?.awsCasSnsTopic
              )
            }
            onChange={handleChangeField}
            isReadOnly={false}
            isRequired={false}
            errorText={
              (!!settingsTouched?.awsCasSnsTopic &&
                settingsError?.awsCasSnsTopic) ||
              ''
            }
            showSpinner={false}
            onBlur={handleBlur}
          />
        </div>
        {enableDataFeedsRevampBehavior && !isTerraformStack ? (
          <div className={classes.dataFeedsRevampFooter}>
            {showCloudFormationDataFeedsSetup ? (
              <>
                <Typography className={classes.sectionText}>
                  Once all three fields are filled, click “Launch CloudFormation
                  Stack” to complete the setup.
                </Typography>
                <CFSButtonV2 cfLink={cftLaunchLink} />
              </>
            ) : null}

            <div data-testid="test-connection-s3">
              <ShowConnectionStatuses
                testConnectionStatus={testConnectionStatus}
                successMessage={
                  showCloudFormationDataFeedsSetup
                    ? 'Labra has successfully connected to your S3 bucket.'
                    : undefined
                }
                failureMessage={
                  showCloudFormationDataFeedsSetup
                    ? isS3BlockedByArnFailure
                      ? 'S3 bucket connection failed because ARN connection failed.'
                      : 'S3 bucket connection failed.'
                    : undefined
                }
              />
            </div>
          </div>
        ) : null}
        {isTerraformStack ? (
          <>
            <ButtonV2
              data-testid="validate-connection-button"
              styleType="outlined"
              className={cx(
                classes.validateConnectionButton,
                loading && classes.validateConnectionButtonLoading
              )}
              onClick={() => void handleValidateTerraformConnection()}
              disabled={loading}
            >
              <span className={classes.validateConnectionButtonContent}>
                <span className={classes.validateConnectionButtonLabel}>
                  {loading ? 'Validating connection' : 'Validate connection'}
                </span>
                {loading ? (
                  <FontAwesomeIcon
                    icon={faLoader}
                    spin
                    className={classes.validateConnectionButtonLoader}
                    data-testid="validate-connection-button-loader"
                  />
                ) : null}
              </span>
            </ButtonV2>
            {showTerraformValidationError ? (
              <Typography
                id="terraformConnectionValidated"
                className={classes.terraformValidationRequiredError}
                data-testid="terraform-connection-validation-error"
              >
                {String(rootSettingsError?.terraformConnectionValidated)}
              </Typography>
            ) : null}
            {terraformValidationPanelVisible ? (
              <div
                className={classes.terraformValidationPanel}
                data-testid="terraform-validation-panel"
              >
                <div className={classes.terraformValidationHeader}>
                  <div className={classes.terraformValidationHeaderLabel}>
                    Validating setup
                  </div>
                  <div className={classes.terraformValidationHeaderLabel}>
                    {terraformValidatedCount}/3 Validated
                  </div>
                </div>
                <div className={classes.terraformValidationStepsList}>
                  {terraformValidationSteps.map(step => (
                    <div
                      key={step.label}
                      className={classes.terraformValidationStepItem}
                    >
                      <div className={classes.terraformValidationStepRow}>
                        {step.status === 'success' && (
                          <FontAwesomeIcon
                            icon={faCircleCheck}
                            className={classes.terraformStepIconSuccess}
                          />
                        )}
                        {step.status === 'failed' && (
                          <FontAwesomeIcon
                            icon={faCircleXmark}
                            className={classes.terraformStepIconFailed}
                          />
                        )}
                        {step.status === 'pending' && (
                          <FontAwesomeIcon
                            icon={faCircleDashed}
                            className={classes.terraformStepIconPending}
                          />
                        )}
                        {step.label}
                      </div>
                      {step.status === 'failed' && (
                        <div className={classes.terraformValidationErrorBox}>
                          <div className={classes.terraformValidationErrorText}>
                            {`${step.errorTitle}\n${step.errorMessage}`}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        ) : null}
        {!showCloudFormationDataFeedsSetup ? (
          <div
            className={classes.subHeadingContainer}
            data-testid="data-feeds-text-and-link"
          >
            <NoteV2
              description={
                <>
                  Go to{' '}
                  <ExternalLink href={newDataFeedProgramLink}>
                    this link
                  </ExternalLink>{' '}
                  , and paste the following values into their corresponding
                  fields in AMMP: S3 Bucket ARN, KMS Key ARN, and SNS Topic ARN.
                </>
              }
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
